/**
 * bloco-parcelamento.tsx — Parcelar a despesa (migration 117, ficha #1).
 *
 * Mora na aba DESPESA, não na Tiny (dele, 26/09): parcelar é coisa do app — o
 * Tiny só recebe o resultado, uma conta por parcela.
 *
 * A despesa que existe vira a parcela 1, e nascem as outras (3× = mais DUAS,
 * não três). O elo é a 1ª parcela: este bloco lista as irmãs e leva a cada uma.
 *
 * Na prévia:
 *   - a condição é a do Tiny (dias a partir da compra: "30 60 90", "0 30 60");
 *   - o TOTAL é o valor da despesa e fica fixo: mexer numa parcela redivide o
 *     resto entre as que ninguém mexeu, com os centavos na última (dele: "se
 *     eu preencho 50% na primeira, ele ajusta as duas restantes");
 *   - vencimento editável, ✕ para tirar uma linha;
 *   - o histórico do Tiny já vem, e cada parcela sai "… - Parcela (1/3)", o
 *     formato que a equipe usa lá.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { CustomCampoData } from '@/components/CustomCamposData';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { doBanco, paraConta } from '@/lib/numero';
import { formatarValor } from './shared';

interface Parcela {
  id: number;
  numero: number;
  quantidade: number;
  vencimento: string | null;
  valor: number;
  envio_tiny: string;
  numero_documento_tiny: string | null;
}

interface Parcelamento {
  parcelada: boolean;
  total?: number;
  pode_desfazer?: boolean;
  parcelas: Parcela[];
}

interface Previa {
  parcelas: { numero: number; vencimento: string; valor: number }[];
  total_original: number;
  historico_base: string | null;
}

/** Uma linha da prévia. `fixa` = alguém digitou o valor: não se redivide. */
interface Linha {
  vencimento: string;
  valor: string;
  fixa: boolean;
}

const NO_TINY: Record<string, string> = {
  enviada: 'No Tiny',
  ja_no_tiny: 'Já estava no Tiny',
  verificar: 'Com cadeado',
  enviando: 'Com cadeado',
};

const centavos = (v: string) => Math.round(paraConta(v) * 100);
const deCentavos = (c: number) => doBanco(c / 100, 2, 'parcela.valor');

/** Redivide o que as linhas FIXAS não levaram entre as outras — os centavos
 *  que sobram vão na última. Nenhuma livre? Todas voltam a ser livres. */
function equilibrar(linhas: Linha[], totalCent: number): Linha[] {
  let ls = linhas;
  if (!ls.some((l) => !l.fixa)) ls = ls.map((l) => ({ ...l, fixa: false }));
  const livres = ls.filter((l) => !l.fixa).length;
  const resto = totalCent - ls.filter((l) => l.fixa).reduce((s, l) => s + centavos(l.valor), 0);
  const base = Math.floor(resto / livres);
  let visto = 0;
  return ls.map((l) => {
    if (l.fixa) return l;
    visto += 1;
    const c = visto === livres ? resto - base * (livres - 1) : base;
    return { ...l, valor: deCentavos(c) };
  });
}

export function BlocoParcelamento({
  despesaId,
  bloqueado,
}: {
  despesaId: string;
  /** A despesa já está no Tiny (ou marcada): não se parcela mais. */
  bloqueado: boolean;
}) {
  const { pode } = useAuth();
  const [p, setP] = useState<Parcelamento | null>(null);
  const [condicao, setCondicao] = useState('');
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [totalCent, setTotalCent] = useState(0);
  const [historico, setHistorico] = useState('');
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    api
      .get<Parcelamento>(`/despesas/${despesaId}/parcelamento`)
      .then(setP)
      .catch(() => setP({ parcelada: false, parcelas: [] }));
  }, [despesaId]);

  async function verPrevia() {
    try {
      const r = await api.get<Previa>(
        `/despesas/${despesaId}/parcelamento/previa?condicao=${encodeURIComponent(condicao)}`,
      );
      const total = Math.round(r.total_original * 100);
      setTotalCent(total);
      setHistorico(r.historico_base ?? '');
      setLinhas(
        equilibrar(
          r.parcelas.map((x) => ({ vencimento: x.vencimento, valor: '', fixa: false })),
          total,
        ),
      );
    } catch (err) {
      setLinhas(null);
      toast.error(err instanceof ApiError ? err.message : 'Condição inválida.');
    }
  }

  function mudarValor(i: number, v: string) {
    setLinhas((ls) =>
      ls ? equilibrar(ls.map((l, j) => (j === i ? { ...l, valor: v, fixa: true } : l)), totalCent) : ls,
    );
  }

  function mudarVencimento(i: number, v: string) {
    setLinhas((ls) => (ls ? ls.map((l, j) => (j === i ? { ...l, vencimento: v } : l)) : ls));
  }

  function tirar(i: number) {
    setLinhas((ls) => (ls ? equilibrar(ls.filter((_, j) => j !== i), totalCent) : ls));
  }

  // A despesa mudou de forma (virou parcela 1, ou juntou de volta): recarrega
  // a página NELA — a 1ª parcela é sempre a que sobra.
  function irPara(id: number) {
    window.location.assign(`/despesas/${id}`);
  }

  async function parcelar() {
    if (!linhas) return;
    setGravando(true);
    try {
      await api.post(`/despesas/${despesaId}/parcelamento`, {
        parcelas: linhas.map((l) => ({ vencimento: l.vencimento, valor: paraConta(l.valor) })),
        historico_base: historico.trim() || null,
      });
      toast.success(`Parcelada em ${linhas.length}.`);
      irPara(Number(despesaId));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível parcelar.');
      setGravando(false);
    }
  }

  async function desfazer() {
    setGravando(true);
    try {
      const r = await api.delete<{ id: number }>(`/despesas/${despesaId}/parcelamento`);
      toast.success('Parcelas juntadas de volta numa despesa só.');
      irPara(r.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível desfazer.');
      setGravando(false);
    }
  }

  if (!p) return null;
  const podeEditar = pode('despesas.lancamento.editar');
  if (!p.parcelada && (bloqueado || !podeEditar)) return null;

  const n = linhas?.length ?? 0;
  const somaCent = (linhas ?? []).reduce((s, l) => s + centavos(l.valor), 0);
  const diferenca = somaCent - totalCent;
  const invalida =
    !linhas || n < 2 || diferenca !== 0 || linhas.some((l) => !l.vencimento || centavos(l.valor) <= 0);

  return (
    <div className="card adminuiux-card mb-3">
      <div className="card-header bg-transparent d-flex align-items-center gap-2">
        <i className="bi bi-layers"></i>
        <h6 className="mb-0 flex-grow-1">Parcelas</h6>
        {p.parcelada && (
          <span className="badge text-bg-light border">
            {p.parcelas.length}× · {formatarValor(p.total ?? 0)}
          </span>
        )}
      </div>
      <div className="card-body">
        {p.parcelada ? (
          <>
            <p className="small text-secondary mb-2">
              Cada parcela é uma despesa, e vai ao Tiny como uma conta própria. Parcela não dá
              baixa pelo app.
            </p>
            <table className="table table-sm align-middle small mb-2">
              <thead>
                <tr className="text-secondary text-uppercase">
                  <th>Parcela</th>
                  <th>Vencimento</th>
                  <th className="text-end">Valor</th>
                  <th>Tiny</th>
                </tr>
              </thead>
              <tbody>
                {p.parcelas.map((x) => (
                  <tr key={x.id} className={String(x.id) === despesaId ? 'table-active' : ''}>
                    <td>
                      {String(x.id) === despesaId ? (
                        <strong>
                          {x.numero}/{x.quantidade} <span className="fw-normal text-secondary">(esta)</span>
                        </strong>
                      ) : (
                        <a href={`/despesas/${x.id}`}>
                          {x.numero}/{x.quantidade}
                        </a>
                      )}
                    </td>
                    <td>{x.vencimento ? formatarData(x.vencimento) : '—'}</td>
                    <td className="text-end">{formatarValor(x.valor)}</td>
                    <td className="text-secondary">
                      {NO_TINY[x.envio_tiny] ?? 'Não enviada'}
                      {x.numero_documento_tiny && (
                        <div className="font-monospace">{x.numero_documento_tiny}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {p.pode_desfazer && podeEditar && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={desfazer}
                disabled={gravando}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>Desfazer — voltar a uma despesa só
              </button>
            )}
          </>
        ) : (
          <>
            <p className="small text-secondary mb-2">
              Compra parcelada? <strong>Esta despesa vira a parcela 1</strong> (o valor
              dela passa a ser o da 1ª parcela) e só as outras são criadas — em 3×, mais
              duas. Cada uma vai ao Tiny como uma conta.
            </p>
            <div className="input-group">
              <div className="form-floating">
                <input
                  id="parcela-condicao"
                  className="form-control"
                  placeholder=" "
                  value={condicao}
                  onChange={(e) => setCondicao(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && condicao.trim()) {
                      e.preventDefault();
                      verPrevia();
                    }
                  }}
                />
                <label htmlFor="parcela-condicao">Dias de cada parcela (como no Tiny)</label>
              </div>
              <button
                type="button"
                className="btn btn-outline-theme"
                onClick={verPrevia}
                disabled={!condicao.trim()}
              >
                Ver parcelas
              </button>
            </div>
            <div className="form-text">
              Um número por parcela, a partir da data da compra: “30 60 90” = três; “0 30 60” =
              uma hoje e duas depois.
            </div>

            {linhas && (
              <div className="mt-3">
                <div className="form-floating mb-2">
                  <input
                    id="parcela-historico"
                    className="form-control"
                    placeholder=" "
                    maxLength={280}
                    value={historico}
                    onChange={(e) => setHistorico(e.target.value)}
                  />
                  <label htmlFor="parcela-historico">Histórico no Tiny</label>
                </div>
                <table className="table table-sm align-middle small mb-2">
                  <thead>
                    <tr className="text-secondary text-uppercase">
                      <th>Parcela</th>
                      <th style={{ width: 170 }}>Vencimento</th>
                      <th style={{ width: 160 }}>Valor</th>
                      <th style={{ width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => (
                      <tr key={i}>
                        <td>
                          <div className="fw-medium">
                            {i + 1}/{n}{' '}
                            {/* Dele, 27/09: "uma já está criada, a gente vai criar
                                mais duas" — a tela tem de dizer qual é qual. */}
                            {i === 0 ? (
                              <span className="badge text-bg-light border fw-normal">esta despesa</span>
                            ) : (
                              <span className="badge bg-theme-1-subtle text-theme-1 fw-normal">nova</span>
                            )}
                          </div>
                          {historico.trim() && (
                            <div className="text-secondary text-truncate" style={{ maxWidth: 260 }}>
                              {historico.trim()} - Parcela ({i + 1}/{n})
                            </div>
                          )}
                        </td>
                        <td>
                          <CustomCampoData valor={l.vencimento} onChange={(v) => mudarVencimento(i, v)} />
                        </td>
                        <td>
                          <CustomCampoNumeroMoeda sm valor={l.valor} onChange={(v) => mudarValor(i, v)} />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-secondary p-0"
                            title={n <= 2 ? 'Parcelamento precisa de pelo menos duas' : 'Tirar esta parcela'}
                            disabled={n <= 2}
                            onClick={() => tirar(i)}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="small mb-2">
                  Total: <strong>{formatarValor(somaCent / 100)}</strong> de{' '}
                  {formatarValor(totalCent / 100)}
                  {diferenca !== 0 && (
                    <span className="text-danger">
                      {' '}
                      — {diferenca > 0 ? 'passa' : 'falta'} {formatarValor(Math.abs(diferenca) / 100)}.
                      Ajuste as parcelas, ou mude o valor da despesa antes.
                    </span>
                  )}
                </p>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-theme"
                    onClick={parcelar}
                    disabled={gravando || invalida}
                  >
                    {gravando && <span className="spinner-border spinner-border-sm me-2" />}
                    Parcelar — criar mais {n - 1}
                  </button>
                  <button type="button" className="btn btn-link" onClick={() => setLinhas(null)}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
